import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import {
  RuleSeverity,
  useCreateConsistencyRuleMutation,
  useCreateConsistencyConditionCheckMutation,
  GQLOperations,
} from 'types/dt-graphql-apollo-v2';
import { DigitalTwin } from 'types/graphql-apollo-v2';
import {
  ConditionOperator,
  ConsistencyCollectionView,
  ConsistencyRuleView,
  ConsistencySchemaFilterView,
  RuleOperator,
} from './types';
import {
  useSchemaData,
  getAttributesForSchemaType,
  resolveSchemaContext,
} from './useSchemaData';
import { LogicalRule, logicalRuleToFilter } from 'utils/rules';
import {
  emptyLogicalRule,
  countRelationalRules,
  hasIncompleteRelationalRule,
} from 'utils/nested-conditions';
import { notification } from 'antd';
import { mapPropertiesToCascaderOptions } from 'utils/filter-builder';
import { safeParseFilter } from './utils/safeParseFilter';
import { DISABLED_TAG } from './helpers';

export { RuleSeverity as Severity };
export { RuleOperator as Operator };

export type ConditionCheckState = {
  id: string;
  sourceAttribute: string;
  operator: ConditionOperator;
  targetAttribute: string;
  isLiteralValue: boolean;
  useTargetSchema: boolean;
};

const MAX_STEP = 7;

type UseNewConsistencyRuleDrawerParams = {
  visible: boolean;
  onClose: VoidFunction;
  collection: ConsistencyCollectionView | null;
  schemaFilters: ConsistencySchemaFilterView[];
  digitalTwin: DigitalTwin;
  initialRule?: ConsistencyRuleView;
};

export const useNewConsistencyRuleDrawer = ({
  visible,
  onClose,
  collection,
  schemaFilters,
  digitalTwin,
  initialRule,
}: UseNewConsistencyRuleDrawerParams) => {
  const { schemas } = useSchemaData(digitalTwin);
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [customerDefinedIdInput, setCustomerDefinedIdInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [severityInput, setSeverityInput] = useState<RuleSeverity>(
    RuleSeverity.Critical
  );
  const [errorMessageInput, setErrorMessageInput] = useState('');
  const [labelListInput, setLabelListInput] = useState<string[]>([]);
  const [sourceSchemaFilterId, setSourceSchemaFilterId] = useState('');
  const [sourceAttribute, setSourceAttribute] = useState('');
  const [operator, setOperator] = useState<RuleOperator>(RuleOperator.IN_SET);
  const [targetSchemaFilterId, setTargetSchemaFilterId] = useState('');
  const [targetAttribute, setTargetAttribute] = useState('');
  const [conditionChecks, setConditionChecks] = useState<ConditionCheckState[]>(
    []
  );
  const [conditionChecksOnly, setConditionChecksOnly] = useState(false);
  const [sourceFiltersTree, setSourceFiltersTreeState] =
    useState<LogicalRule>(emptyLogicalRule);
  const [sourceFiltersDirty, setSourceFiltersDirty] = useState(false);
  const [targetFiltersTree, setTargetFiltersTreeState] =
    useState<LogicalRule>(emptyLogicalRule);
  const [targetFiltersDirty, setTargetFiltersDirty] = useState(false);

  const setSourceFiltersTree = (tree: LogicalRule) => {
    setSourceFiltersTreeState(tree);
    setSourceFiltersDirty(true);
  };
  const setTargetFiltersTree = (tree: LogicalRule) => {
    setTargetFiltersTreeState(tree);
    setTargetFiltersDirty(true);
  };

  const apolloClient = useApolloClient();
  const [createRule] = useCreateConsistencyRuleMutation();
  const [createConditionCheck] = useCreateConsistencyConditionCheckMutation();

  // Derived data
  const sourceSchemaFilter = schemaFilters.find(
    (r) => r.externalId === sourceSchemaFilterId
  );
  const targetSchemaFilter = schemaFilters.find(
    (r) => r.externalId === targetSchemaFilterId
  );

  const sourceAttributes: string[] = sourceSchemaFilter
    ? getAttributesForSchemaType(sourceSchemaFilter.schemaType, schemas)
    : [];
  const targetAttributes: string[] = targetSchemaFilter
    ? getAttributesForSchemaType(targetSchemaFilter.schemaType, schemas)
    : [];

  const {
    schemaTypes: sourceSchemaTypes,
    selectedType: sourceSelectedType,
    typeProperties: sourceTypeProperties,
  } = resolveSchemaContext(sourceSchemaFilter, schemas);
  const {
    schemaTypes: targetSchemaTypes,
    selectedType: targetSelectedType,
    typeProperties: targetTypeProperties,
  } = resolveSchemaContext(targetSchemaFilter, schemas);

  const sourceConditionAttributeOptions = mapPropertiesToCascaderOptions(
    sourceSelectedType?.properties ?? [],
    sourceSchemaTypes
  );
  const targetConditionAttributeOptions = mapPropertiesToCascaderOptions(
    targetSelectedType?.properties ?? [],
    targetSchemaTypes
  );

  const sourceFiltersCount = countRelationalRules(sourceFiltersTree);
  const targetFiltersCount = countRelationalRules(targetFiltersTree);

  // Validation
  const hasInvalidName = !nameInput.trim();
  const hasInvalidErrorMessage = !errorMessageInput.trim();
  const hasInvalidSource = !sourceSchemaFilterId || !sourceAttribute;
  const hasInvalidTarget = !targetSchemaFilterId || !targetAttribute;
  const hasIncompleteSourceFilter =
    hasIncompleteRelationalRule(sourceFiltersTree);
  const hasIncompleteTargetFilter =
    hasIncompleteRelationalRule(targetFiltersTree);
  const hasInvalidValues =
    hasInvalidName ||
    hasInvalidErrorMessage ||
    hasInvalidSource ||
    hasInvalidTarget ||
    hasIncompleteSourceFilter ||
    hasIncompleteTargetFilter;

  const onResetDrawer = () => {
    setNameInput('');
    setCustomerDefinedIdInput('');
    setDescriptionInput('');
    setSeverityInput(RuleSeverity.Critical);
    setErrorMessageInput('');
    setLabelListInput([]);
    setSourceSchemaFilterId('');
    setSourceAttribute('');
    setOperator(RuleOperator.IN_SET);
    setTargetSchemaFilterId('');
    setTargetAttribute('');
    setConditionChecks([]);
    setConditionChecksOnly(false);
    setSourceFiltersTreeState(emptyLogicalRule);
    setSourceFiltersDirty(false);
    setTargetFiltersTreeState(emptyLogicalRule);
    setTargetFiltersDirty(false);
    setCurrentStep(0);
  };

  useEffect(() => {
    if (!visible) return;
    if (initialRule) {
      setNameInput(`Copy of ${initialRule.name}`);
      setCustomerDefinedIdInput('');
      setDescriptionInput(initialRule.description ?? '');
      setSeverityInput(initialRule.severity);
      setErrorMessageInput(initialRule.errorMessage ?? '');
      setLabelListInput(
        (initialRule.tags ?? []).filter((tag) => tag !== DISABLED_TAG)
      );
      setSourceSchemaFilterId(initialRule.sourceSchemaFilter.externalId);
      setSourceAttribute(initialRule.sourceAttribute);
      setOperator(initialRule.operator);
      setTargetSchemaFilterId(initialRule.targetSchemaFilter.externalId);
      setTargetAttribute(initialRule.targetAttribute);
      setSourceFiltersTreeState(safeParseFilter(initialRule.sourceFilters));
      setSourceFiltersDirty(initialRule.sourceFilters != null);
      setTargetFiltersTreeState(safeParseFilter(initialRule.targetFilters));
      setTargetFiltersDirty(initialRule.targetFilters != null);
      setConditionChecks(
        initialRule.conditionChecks.map((cc) => ({
          id: uuidv4(),
          sourceAttribute: cc.sourceAttribute,
          operator: cc.operator,
          targetAttribute: cc.targetAttribute,
          isLiteralValue: cc.isLiteralValue,
          useTargetSchema: cc.useTargetSchema,
        }))
      );
      setConditionChecksOnly(initialRule.conditionChecksOnly);
      setCurrentStep(0);
    } else {
      onResetDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, MAX_STEP));
  };

  const onCreateRule = async () => {
    if (creating || hasInvalidValues || !collection) return;
    setCreating(true);

    try {
      const ruleId = uuidv4();

      await createRule({
        variables: {
          id: ruleId,
          name: nameInput.trim(),
          customerDefinedId: customerDefinedIdInput.trim() || undefined,
          description: descriptionInput || undefined,
          sourceAttribute,
          sourceFilters: sourceFiltersDirty
            ? JSON.stringify(
                logicalRuleToFilter(sourceFiltersTree, sourceTypeProperties)
              )
            : null,
          operator,
          targetAttribute,
          targetFilters: targetFiltersDirty
            ? JSON.stringify(
                logicalRuleToFilter(targetFiltersTree, targetTypeProperties)
              )
            : null,
          severity: severityInput,
          errorMessage: errorMessageInput,
          tags: labelListInput.length > 0 ? labelListInput : undefined,
          conditionChecksOnly,
          projectId: collection.externalId,
          collectionId: collection.externalId,
          sourceSchemaFilterId,
          targetSchemaFilterId,
        },
      });

      await Promise.all(
        conditionChecks.map((check) =>
          createConditionCheck({
            variables: {
              id: uuidv4(),
              sourceAttribute: check.sourceAttribute,
              operator: check.operator,
              targetAttribute: check.targetAttribute,
              isLiteralValue: check.isLiteralValue ? true : undefined,
              useTargetSchema: check.useTargetSchema ? true : undefined,
              ruleId,
            },
          })
        )
      );

      await apolloClient.refetchQueries({
        include: [GQLOperations.Query.GetConsistencyCollections],
      });

      notification.success({
        message: 'Comparison rule created',
        description: `Successfully created the comparison rule "${nameInput.trim()}"`,
      });

      onResetDrawer();
      onClose();
    } catch (err: unknown) {
      notification.error({
        message: 'Could not create comparison rule',
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setCreating(false);
    }
  };

  const onCancelHandler = () => {
    if (creating) return;
    setCurrentStep(0);
    onClose();
  };

  const handleSourceSchemaFilterChange = (schemaFilterId: string) => {
    setSourceSchemaFilterId(schemaFilterId);
    setSourceAttribute('');
  };

  const handleTargetSchemaFilterChange = (schemaFilterId: string) => {
    setTargetSchemaFilterId(schemaFilterId);
    setTargetAttribute('');
  };

  const addConditionCheck = () => {
    setConditionChecks((prev) => [
      ...prev,
      {
        id: uuidv4(),
        sourceAttribute: '',
        operator: ConditionOperator.EQUALS,
        targetAttribute: '',
        isLiteralValue: false,
        useTargetSchema: false,
      },
    ]);
  };

  const removeConditionCheck = (id: string) => {
    setConditionChecks((prev) => {
      const next = prev.filter((check) => check.id !== id);
      if (next.length === 0) {
        setConditionChecksOnly(false);
      }
      return next;
    });
  };

  const updateConditionCheck = <
    K extends keyof Omit<ConditionCheckState, 'id'>,
  >(
    id: string,
    field: K,
    value: ConditionCheckState[K]
  ) => {
    setConditionChecks((prev) =>
      prev.map((check) =>
        check.id === id ? { ...check, [field]: value } : check
      )
    );
  };

  const toggleConditionCheckIsLiteralValue = (id: string) => {
    setConditionChecks((prev) =>
      prev.map((check) => {
        if (check.id !== id) return check;
        const next = !check.isLiteralValue;
        return {
          ...check,
          isLiteralValue: next,
          useTargetSchema: next ? check.useTargetSchema : false,
        };
      })
    );
  };

  const toggleConditionCheckUseTargetSchema = (id: string) => {
    setConditionChecks((prev) =>
      prev.map((check) =>
        check.id === id
          ? {
              ...check,
              useTargetSchema: !check.useTargetSchema,
              sourceAttribute: '',
            }
          : check
      )
    );
  };

  return {
    // State
    currentStep,
    setCurrentStep,
    creating,
    nameInput,
    setNameInput,
    customerDefinedIdInput,
    setCustomerDefinedIdInput,
    descriptionInput,
    setDescriptionInput,
    severityInput,
    setSeverityInput,
    errorMessageInput,
    setErrorMessageInput,
    labelListInput,
    setLabelListInput,
    sourceSchemaFilterId,
    sourceAttribute,
    setSourceAttribute,
    operator,
    setOperator,
    targetSchemaFilterId,
    targetAttribute,
    setTargetAttribute,

    // Derived data
    sourceSchemaFilter,
    targetSchemaFilter,
    sourceAttributes,
    targetAttributes,
    sourceSchemaTypes,
    sourceConditionAttributeOptions,
    targetConditionAttributeOptions,
    sourceSelectedType,
    targetSchemaTypes,
    targetSelectedType,

    // Per-rule filters
    sourceFiltersTree,
    setSourceFiltersTree,
    sourceFiltersCount,
    targetFiltersTree,
    setTargetFiltersTree,
    targetFiltersCount,

    // Validation
    hasInvalidName,
    hasInvalidErrorMessage,
    hasInvalidSource,
    hasInvalidTarget,
    hasIncompleteSourceFilter,
    hasIncompleteTargetFilter,
    hasInvalidValues,

    // Equality checks
    conditionChecks,
    conditionChecksOnly,
    setConditionChecksOnly,
    addConditionCheck,
    removeConditionCheck,
    updateConditionCheck,
    toggleConditionCheckIsLiteralValue,
    toggleConditionCheckUseTargetSchema,

    // Handlers
    nextStep,
    onCreateRule,
    onCancelHandler,
    handleSourceSchemaFilterChange,
    handleTargetSchemaFilterChange,
  };
};
